import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ThemeToggle from "@/theme/ThemeToggle";
import AppLauncher from "@/components/AppLauncher";
import Button from "@/components/Button";
import Skeleton from "@/components/Skeleton";
import Logo from "@/components/Logo";
import Icon from "@/components/Icon";
import UserMenu from "@/components/UserMenu";
import { colorVar } from "@/components/CourseContextBar";
import { listEnrolledCourses, joinCourse } from "@/api/course";
import type { EnrolledCourse } from "@/types";

/** Hub élève : la liste des cours auxquels il est inscrit + rejoindre par code. */
export default function CourseHub() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<EnrolledCourse[] | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);

  const reload = () => listEnrolledCourses().then(setCourses).catch(() => setCourses([]));
  useEffect(() => { reload(); }, []);

  const join = async () => {
    const c = code.trim();
    if (!c) return;
    setJoining(true); setError(null);
    try {
      const course = await joinCourse(c);
      setCode("");
      await reload();
      navigate(`/exercises/c/${course.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Code invalide.");
    } finally {
      setJoining(false);
    }
  };

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <Link to="/" style={styles.logo} title="Retour à l'accueil">
          <Logo size={24} />
          <span>Mes cours</span>
        </Link>
        <div style={styles.headerRight}>
          <ThemeToggle />
          <AppLauncher />
          <UserMenu />
        </div>
      </header>

      <main style={styles.main}>
        <h1 style={styles.title}>Mes cours</h1>

        {courses === null ? (
          <div style={styles.grid}>
            {Array.from({ length: 2 }).map((_, i) => <div key={i} style={styles.card}><Skeleton height={70} /></div>)}
          </div>
        ) : courses.length === 0 ? (
          <p style={styles.hint}>Tu n'es inscrit à aucun cours. Rejoins-en un avec le code donné par ton enseignant.</p>
        ) : (
          <div style={styles.grid}>
            {courses.map((c) => (
              <button key={c.id} className="card" style={{ ...styles.card, borderLeft: `4px solid ${colorVar(c.color)}` }}
                      onClick={() => navigate(`/exercises/c/${c.id}`)}>
                <span style={{ ...styles.dot, background: colorVar(c.color) }} />
                <div style={{ textAlign: "left" }}>
                  <h2 style={styles.cardTitle}>{c.title}</h2>
                  {c.teacher && <p style={styles.cardTeacher}>par {c.teacher}</p>}
                </div>
                <Icon name="next" size={18} style={{ marginLeft: "auto", color: "var(--text-muted)" }} />
              </button>
            ))}
          </div>
        )}

        {/* Rejoindre un cours par code */}
        <div style={styles.joinBox}>
          <h3 style={styles.joinTitle}>Rejoindre un cours</h3>
          <p style={styles.joinHint}>Saisis le code communiqué par ton enseignant.</p>
          <div style={styles.joinRow}>
            <input
              style={styles.codeInput}
              placeholder="Code (ex. ABC123)"
              value={code}
              onChange={(e) => { setCode(e.target.value.toUpperCase()); setError(null); }}
              onKeyDown={(e) => { if (e.key === "Enter") join(); }}
            />
            <Button variant="primary" onClick={join} disabled={joining || !code.trim()}>
              {joining ? <span className="spin" /> : "Rejoindre"}
            </Button>
          </div>
          {error && <p style={styles.error}><Icon name="alert" size={14} style={{ color: "var(--red)" }} /> {error}</p>}
        </div>
      </main>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { minHeight: "100vh", background: "var(--bg-base)", color: "var(--text)" },
  header: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "16px 32px", background: "var(--bg-bar)", borderBottom: "1px solid var(--border-soft)",
  },
  logo: { color: "var(--blue)", fontSize: 20, display: "inline-flex", alignItems: "center", gap: 10, textDecoration: "none" },
  headerRight: { display: "flex", alignItems: "center", gap: 12 },
  main: { padding: "40px 32px", maxWidth: 820, margin: "0 auto" },
  title: { fontSize: 24, fontWeight: 700, margin: "0 0 24px" },
  hint: { color: "var(--text-muted)", fontSize: 14 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 420px))", gap: 16 },
  card: {
    display: "flex", alignItems: "center", gap: 14, background: "var(--bg-panel)",
    border: "1px solid var(--border-soft)", borderRadius: "var(--radius-lg)", padding: "18px 20px",
    color: "var(--text)", cursor: "pointer", textAlign: "left",
  },
  dot: { width: 14, height: 14, borderRadius: "50%", flexShrink: 0 },
  cardTitle: { margin: 0, fontSize: 16 },
  cardTeacher: { margin: "2px 0 0", fontSize: 12.5, color: "var(--text-muted)" },
  joinBox: { marginTop: 36, padding: "20px 22px", background: "var(--bg-panel)", border: "1px solid var(--border-soft)", borderRadius: "var(--radius-lg)", maxWidth: 420 },
  joinTitle: { margin: "0 0 4px", fontSize: 16 },
  joinHint: { margin: "0 0 12px", fontSize: 13, color: "var(--text-muted)" },
  joinRow: { display: "flex", gap: 8 },
  codeInput: {
    flex: 1, background: "var(--bg-base)", border: "1px solid var(--border)", borderRadius: "var(--radius)",
    color: "var(--text)", padding: "8px 12px", fontFamily: "var(--font-mono)", fontSize: 15, letterSpacing: 1,
  },
  error: { display: "flex", alignItems: "center", gap: 6, color: "var(--red)", fontSize: 13, margin: "10px 0 0" },
};
